import PropTypes from "prop-types";
import React, { useEffect, useCallback, useRef, useState } from "react";
import classnames from "classnames";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import globals from "web/script/modules/globals";
import browser from "web/script/utils/browser.js";
import TrackedButton from "web/react/components/tracked-button/tracked-button";
import styles from "./rail.css";


function Rail({
    analyticsLabel,
    children,
    className,
    railClassName,
    railsMoreButtonPlacement,
    railMoreVariant,
    seeAllLink,
    sendAnalytics,
    showExtraRailItem,
    smallRail,
    title,
}) {
    let railRef = useRef(null);
    let [shouldShowArrows, setShouldShowArrows] = useState(false);
    let [firstInteraction, setFirstInteraction] = useState(false);
    let [seenAll, setSeenAll] = useState(false);
    let [hideLeftArrow, setHideLeftArrow] = useState(true);
    let [hideRightArrow, setHideRightArrow] = useState(true);
    let isLeft = railsMoreButtonPlacement === "left";
    let headerClasses = classnames({
        [styles.smallLeftHeader]: isLeft && smallRail,
        [styles.defaultLeftHeader]: isLeft && !smallRail,
        [styles.smallHeader]: !isLeft && smallRail,
        [styles.defaultHeader]: !isLeft && !smallRail,
    });

    let moreLabel;
    if (railMoreVariant === "show_more") {
        moreLabel = gettext("general.show_more");
    } else if (railMoreVariant === "shop_all") {
        moreLabel = gettext("general.shop_all");
    } else {
        moreLabel = gettext("general.see_all");
    }

    const setArrowVisibility = useCallback(() => {
        let currentRef = railRef.current;
        setHideLeftArrow(currentRef.scrollLeft === 0);
        if (
            currentRef.scrollLeft + currentRef.offsetWidth === currentRef.scrollWidth ||
            currentRef.scrollWidth === currentRef.offsetWidth
        ) {
            setHideRightArrow(true);
            if (sendAnalytics && !seenAll && firstInteraction) {
                setSeenAll(true);
                analytics.event("rail", "seen last item", analyticsLabel, true);
            }
        } else {
            setHideRightArrow(false);
        }
    }, [seenAll, analyticsLabel, sendAnalytics, firstInteraction]);

    function scrollInteraction() {
        if (sendAnalytics && !firstInteraction) {
            setFirstInteraction(true);
            analytics.event("rail", "scroll", analyticsLabel);
        }
        setArrowVisibility();
    }

    useEffect(() => {
        if (shouldShowArrows) {
            // Only do this on desktop ...
            globals.window.addEventListener("resize", setArrowVisibility);
            return () => {
                globals.window.removeEventListener("resize", setArrowVisibility);
            };
        }
    }, [setArrowVisibility, shouldShowArrows]);

    useEffect(() => {
        setArrowVisibility();
    });

    useEffect(() => {
        setShouldShowArrows(!browser.hasTouch);
    }, []);

    function move(direction) {
        let currentRef = railRef.current;
        let width = currentRef.offsetWidth;

        if (sendAnalytics && !firstInteraction) {
            setFirstInteraction(true);
            analytics.event("rail", `click ${direction}`, analyticsLabel);
        }

        let scrollAmount;
        if (direction === "left") {
            scrollAmount = -(width / 1.5);
        } else if (direction === "right") {
            scrollAmount = width / 1.5;
        }
        browser.animate(currentRef, "scrollLeft", scrollAmount, 300);
    }

    return (
        <div className={className}>
            <div className={headerClasses}>
                <h3 className={smallRail ? styles.smallTitle : styles.defaultTitle}>{title}</h3>
                {seeAllLink && (
                    <TrackedButton
                        href={seeAllLink}
                        eventCategory="rail"
                        eventAction="click see all"
                        eventLabel={analyticsLabel}
                        className={styles.seeAll}
                    >
                        <span className={styles.seeAllText}>{moreLabel}</span>
                        <SVGIcon className={styles.seeAllChevron} name="round-chevron" />
                    </TrackedButton>
                )}
            </div>
            <div className={styles.content}>
                <ol
                    ref={railRef}
                    className={classnames(styles.rail, railClassName)}
                    onScroll={scrollInteraction}
                >
                    {React.Children.map(children, (child) => {
                        return <li className={styles.listItem}>{child}</li>;
                    })}
                    {seeAllLink && showExtraRailItem && (
                        <li className={classnames(styles.lastCardWrapper, styles.listItem)}>
                            <TrackedButton
                                href={seeAllLink}
                                eventCategory="rail"
                                eventAction="click see all"
                                eventLabel={analyticsLabel}
                                className={classnames(styles.lastCard)}
                            >
                                <span className={styles.seeAll}>
                                    {moreLabel}
                                    <SVGIcon
                                        className={styles.seeAllChevron}
                                        name="round-chevron"
                                    />
                                </span>
                            </TrackedButton>
                        </li>
                    )}
                </ol>

                {shouldShowArrows && !hideLeftArrow && (
                    <button
                        type="button"
                        className={smallRail ? styles.smallLeftArrow : styles.leftArrow}
                        onClick={() => move("left")}
                    >
                        <SVGIcon name="round-chevron" rotate={180} />
                    </button>
                )}
                {shouldShowArrows && !hideRightArrow && (
                    <button
                        type="button"
                        className={smallRail ? styles.smallRightArrow : styles.rightArrow}
                        onClick={() => move("right")}
                    >
                        <SVGIcon name="round-chevron" />
                    </button>
                )}
            </div>
        </div>
    );
}

Rail.defaultProps = {
    sendAnalytics: true,
    smallRail: false,
    showExtraRailItem: false,
    railsMoreButtonPlacement: "right",
};

Rail.propTypes = {
    analyticsLabel: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    railClassName: PropTypes.string,
    railsMoreButtonPlacement: PropTypes.string,
    railMoreVariant: PropTypes.string,
    seeAllLink: PropTypes.string,
    sendAnalytics: PropTypes.bool,
    showExtraRailItem: PropTypes.bool,
    smallRail: PropTypes.bool,
    title: PropTypes.string.isRequired,
};

export default Rail;
